import React from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LockIcon from "@mui/icons-material/Lock";
import { Box, Typography, Chip, Tooltip, Grid } from "@mui/material";
import type { IntLimitFeatureInput, OverrunLimitFeatureInput } from "components/Entitlements/EntitlementFormInputs";
import { Fieldset } from "components/form/Fieldset";

function isIntLimitInput(x: unknown): x is IntLimitFeatureInput {
  return typeof x === "object" && x !== null && "isUnlimited" in x;
}

function isOverrunLimitInput(x: unknown): x is OverrunLimitFeatureInput {
  return isIntLimitInput(x) && "countDisabledUnits" in x;
}

export interface ReadOnlyEntitlementFieldProps {
  name: string;
  value: unknown;
  fieldType: "int" | "boolean" | "intOrUnlimited" | "intWithOverrun";
  description?: string;
  isConfigurable?: boolean;
}

export function ReadOnlyEntitlementField({
  name,
  value,
  fieldType,
  description,
  isConfigurable = false,
}: ReadOnlyEntitlementFieldProps) {
  const renderValue = () => {
    if (value === null || value === undefined) {
      return (
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
          }}
        >
          Not configured
        </Typography>
      );
    }

    switch (fieldType) {
      case "boolean":
        return (
          <Chip
            label={value ? "Enabled" : "Disabled"}
            color={value ? "success" : "default"}
            size="small"
            variant="outlined"
          />
        );

      case "int":
        return (
          <Typography variant="body1" component="span">
            {typeof value === "number" ? value.toLocaleString() : String(value)}
          </Typography>
        );

      case "intOrUnlimited":
      case "intWithOverrun": {
        if (isIntLimitInput(value)) {
          if (value.isUnlimited) {
            return <Chip label="Unlimited" color="primary" size="small" variant="outlined" />;
          }

          const limitText = value.limit !== undefined ? value.limit.toLocaleString() : "0";
          const overrunText =
            isOverrunLimitInput(value) && value.overrun !== undefined ? ` (+${value.overrun} overrun)` : "";
          const countDisabledUnits = isOverrunLimitInput(value) && value.countDisabledUnits;

          return (
            <Typography variant="body1" component="span">
              {limitText}
              {overrunText}
              {countDisabledUnits && (
                <Tooltip title="Counts disabled units">
                  <InfoOutlinedIcon
                    fontSize="small"
                    sx={{
                      ml: 1,
                      verticalAlign: "text-bottom",
                      color: "text.secondary",
                    }}
                  />
                </Tooltip>
              )}
            </Typography>
          );
        }

        // Fallback for simple number values
        return (
          <Typography variant="body1" component="span">
            {value.toLocaleString()}
          </Typography>
        );
      }

      default:
        return (
          <Typography variant="body1" component="span">
            {String(value)}
          </Typography>
        );
    }
  };

  const tooltipText = !isConfigurable
    ? "This value is determined by the plan and cannot be modified"
    : description || "";

  return (
    <Fieldset title={name}>
      <Box sx={{ position: "relative" }}>
        {!isConfigurable && (
          <Tooltip title={tooltipText}>
            <LockIcon
              fontSize="small"
              sx={{
                color: "text.secondary",
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%)",
                right: 8,
                zIndex: 1,
              }}
            />
          </Tooltip>
        )}
        <Grid
          container
          spacing={2}
          sx={{
            alignItems: "center",
          }}
        >
          <Grid size="grow">{renderValue()}</Grid>
        </Grid>
        {description && isConfigurable && (
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              mt: 1,
              display: "block",
            }}
          >
            {description}
          </Typography>
        )}
      </Box>
    </Fieldset>
  );
}
